<template>
  <div class="columns">
    <div class="column is-four-fifths">
      <div v-if="!updatingSoftware && !selectingFeatures">
        <detail-field-2>
          <template v-slot:label>Current Firmware</template>
          <template v-slot:value>{{ terminal?.software?.currentVersion || 'N/A' }}</template>
        </detail-field-2>
        <detail-field-2>
          <template v-slot:label>Last Updated On</template>
          <template v-slot:value>
            {{ terminal?.software?.lastUpdatedOn 
              ? `${format(addMinutes(new Date(terminal?.software?.lastUpdatedOn), new Date(terminal?.software?.lastUpdatedOn).getTimezoneOffset()), 'yyyy-MM-dd HH:mm:ss')} (UTC)`
              : 'N/A' 
            }}
          </template>
        </detail-field-2>
        <detail-field-2>
          <template v-slot:label>Software Locked</template>
          <template v-slot:value>
            <span :class="{ 'has-text-danger': terminal?.software?.locked, 'has-text-grey-dark': !terminal?.software?.locked }" class="has-text-weight-semibold">{{ terminal?.software?.locked ? 'Yes' : 'No' }}</span>
          </template>
        </detail-field-2>
        <detail-field-2>
          <template v-slot:label>Update Scheduled</template>
          <template v-slot:value>
            <span :class="{ 'has-text-warning': !!(terminal?.software?.targetVersion), 'has-text-grey-dark': !(terminal?.software?.targetVersion) }" class="has-text-weight-semibold">{{ !!(terminal?.software?.targetVersion) ? `Yes - ${terminal?.software?.targetVersion}` : 'No' }}</span>
          </template>
        </detail-field-2>
        <detail-field-2>
          <template v-slot:label>Last Checkin</template>
          <template v-slot:value>
            {{ terminal?.software?.lastCheckin 
              ? `${format(addMinutes(new Date(terminal?.software?.lastCheckin), new Date(terminal?.software?.lastCheckin).getTimezoneOffset()), 'yyyy-MM-dd HH:mm:ss')} (UTC)`
              : 'N/A' 
            }}
          </template>
        </detail-field-2>
        <detail-field-2>
          <template v-slot:label>Features</template>
          <template v-slot:value>
            {{ terminal?.features?.join(', ') || 'N/A' }}
          </template>
        </detail-field-2>
      </div>
      <div class="is-relative" v-if="selectingFeatures">
        <b-loading :is-full-page="false" v-model="isGeneratingCertificate" :can-cancel="false">
          <b-icon pack="far" icon="spinner-third" size="is-large" custom-class="fa-spin" />
        </b-loading>
        <div class="kpt-terminal-feature-grid" v-for="feature in enabledFeatureTypes" :key="feature.id">
          <b-field :label="feature.name">
            <b-switch v-model="selectedFeatures" :native-value="feature.shortcode" @input="toggleFeature" :disabled="isGeneratingCertificate">
              {{ selectedFeatures.includes(feature) ? 'Yes' : 'No'}}
            </b-switch>
          </b-field>
          <b-message type="is-light" class="content">
            <p>{{ feature.description}}</p>
          </b-message>
        </div>
      </div>
      <div v-if="updatingSoftware">
        <b-message type="is-light" class="content">
          <p>You are updating terminal <strong>{{terminal?.name}}</strong> with antenna serial <strong>{{terminal?.hardware?.antenna?.serial}}</strong> with the existing software version <strong>{{terminal?.software?.currentVersion}}</strong>.</p>
          <p>The antenna for this terminal will be updated the next time it checks in with Kymeta Cloud Services.</p>
        </b-message>
        <label class="label">Choose the target version:</label>
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <b-tooltip type="is-info" label="There are no available updates for this device." :active="isSoftwareVersionsDropdownDisabled" position="is-right">
                <b-select v-model="targetVersion" :disabled="isSoftwareVersionsDropdownDisabled">
                  <option v-for="opt in softwareVersionsForThisTerminal" :key="opt.id" :value="opt">{{opt.version}}</option>
                </b-select>
              </b-tooltip>
            </div>
            <div class="level-item" v-if="targetVersion">
              Released: {{format(new Date(targetVersion.releaseDate), 'yyyy-MM-dd')}}
            </div>
          </div>
        </div>
        <div v-if="targetVersion">
          <b-notification type="is-info" :closable="false">
            <p><strong>Note:</strong> The minimum required version for the {{targetVersion.version}} release is {{targetVersion.minimumRequiredVersion}}. Updating from releases older than {{targetVersion.minimumRequiredVersion}} may break the antenna.</p>
          </b-notification>
        </div>
      </div>
    </div>
    <div class="column is-one-fifth">
      <div class="px-4 action-links-container">
        <div v-if="!updatingSoftware && !selectingFeatures">
          <p v-if="!terminal?.software?.targetVersion"><a @click="openSoftwareUpdate">Update Software</a></p>
          <p v-else>
            <a v-if="!isUpdatingSoftware" @click="cancelSoftwareUpdate">Cancel Software Update</a>
            <span v-else>Cancelling update... please wait.</span>
          </p>
          <p v-if="!terminal?.software?.locked" class="mt-2">
            <a v-if="!isTogglingSoftwareLock" @click="toggleSoftwareLock">Lock Software</a>
            <span v-else>Locking software... please wait.</span>
          </p>
          <p v-else class="mt-2">
            <a v-if="!isTogglingSoftwareLock" @click="toggleSoftwareLock">Unlock Software</a>
            <span v-else>Unlocking software... please wait.</span>
          </p>
          <p class="mt-2" @click="openSelectingFeatures"><a>Select Features</a></p>
          <p class="mt-2">
            <span v-if="!terminal.publicKey" class="has-text-grey" title="Certificate is not available for this terminal.">Download Certificate</span>
            <a v-else @click="downloadCertificate">Download Certificate</a>
          </p>
        </div>
        <div v-if="selectingFeatures">
          <b-message :active.sync="hasUnsavedChanges" type="is-warning">
            You have unsaved changes, please "Save" before downloading.
          </b-message>
          <p><a @click="saveFeatures">Save Features</a></p>
          <p class="mt-2"><a @click="cancelSelectingFeatures">Cancel</a></p>
        </div>
        <div v-if="updatingSoftware">
          <p>
            <a v-if="!isUpdatingSoftware" @click="confirmSoftwareUpdate">Confirm Update</a>
            <span v-else>Queueing update... please wait.</span>
          </p>
          <p class="mt-2"><a @click="closeSoftwareUpdate">Cancel</a></p>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import download from 'downloadjs';
import { ref, computed, getCurrentInstance } from 'vue';
import { format, addMinutes } from 'date-fns';
import { useTerminalsStore } from '../terminals-store';
import { useDevicesStore } from '../../devices/device-store';

const app = getCurrentInstance();
const store = useTerminalsStore();
const devicesStore = useDevicesStore();
const terminal = computed(() => store.state.terminal);

const selectingFeatures = ref(false);
const selectedFeatures = ref([]);
const enabledFeatureTypes = computed(() => devicesStore.state.features.filter(ft => ft.enabled));
const isGeneratingCertificate = ref(false);
const softwareVersionsForThisTerminal = computed(() => store.state.terminalSpecificSoftwareVersions);
const isSoftwareVersionsDropdownDisabled = computed(() => !softwareVersionsForThisTerminal.value.length);

const hasUnsavedChanges = computed(() => {
  // compare selection and device features to determine differences
  const selectedFeats = selectedFeatures.value;
  const deviceFeats = terminal.value.features || [];
  const selectionDiff = selectedFeats.filter(f => !deviceFeats.includes(f));
  const deviceDiff = deviceFeats.filter(f => !selectedFeats.includes(f));
  // toggle unsaved changes message when necessary
  return selectionDiff.length > 0 || deviceDiff.length > 0;
});

// #region Features
const openSelectingFeatures = () => {
  selectedFeatures.value = terminal.value.features ? [...terminal.value.features] : [];
  selectingFeatures.value = true;
}
const cancelSelectingFeatures = () => {
  selectedFeatures.value = [];
  selectingFeatures.value = false;
}
const toggleFeature = () => {
  // KyAnalytics client plugin, event tracker
  app.proxy.$kya.event('terminals', 'toggleDeviceFeature', 'Sets device feature value', { terminalId: terminal.value.id });
}
const saveFeatures = async () => {
  if (isGeneratingCertificate.value) return; // prevent spam action spam
  // generate the certificate
  isGeneratingCertificate.value = true;
  // eliminate empty/nulls 
  const features = selectedFeatures.value.filter(sf => sf);
  // generate the feature certificate
  const result = await store.dispatch('generateFeaturesCertificate', features);
  isGeneratingCertificate.value = false;
  // check for failed request/cert generation
  if (!result) {
    // request failed... check again if we need to show differences message
    app.proxy.$buefy.toast.open({
      duration: 2500,
      message: 'Error encountered while attempting to generate certificate.',
      type: 'is-danger'
    });
    return;
  }
  // inform user of success
  app.proxy.$buefy.toast.open({
    message: 'Feature Certificate Generated',
    type: 'is-success'
  });
  // KyAnalytics client plugin, event tracker
  app.proxy.$kya.event('terminals', 'generateFeatureCertificate', 'Feature certificate generated', { terminalId: terminal.value.id });
  selectingFeatures.value = false;
}
// #endregion

// #region Software Update
const isTogglingSoftwareLock = ref(false);
const toggleSoftwareLock = async () => {
  if (!terminal.value.software) return;
  const cmd = terminal.value.software.locked ? 'unlockSoftware' : 'lockSoftware';
  isTogglingSoftwareLock.value = true;
  await store.dispatch('executeTerminalCommand', { id: terminal.value.id, cmd })
  isTogglingSoftwareLock.value = false;
  app.proxy.$kya.event('terminals', 'toggledSoftwareLock', 'Locked or unlocked software updates', { terminalId: terminal.value.id });
}

const updatingSoftware = ref(false);
const targetVersion = ref(null);
const isUpdatingSoftware = ref(false);
const openSoftwareUpdate = async () => {
  updatingSoftware.value = true;
  if (!softwareVersionsForThisTerminal.value.length) { // only fetch list doesn't already exist
    await store.dispatch('fetchSoftwareVersions', terminal?.value?.software?.currentVersion);
  }
  app.proxy.$kya.event('terminals', 'openedSoftwareUpdate', 'Opened software update panel', { terminalId: terminal.value.id });
}
const closeSoftwareUpdate = () => {
  updatingSoftware.value = false;
}
const confirmSoftwareUpdate = async () => {
  if (isUpdatingSoftware.value) return; // prevent spam action spam
  isUpdatingSoftware.value = true;
  const result = await store.dispatch('executeTerminalCommand', { id: terminal.value.id, cmd: 'updateSoftware', data: targetVersion.value.version });
  isUpdatingSoftware.value = false;
  if (!result) {
    app.proxy.$buefy.toast.open({ message: 'Error encountered while attempting to queue software update.', type: 'is-danger' });
    return;
  }
  app.proxy.$buefy.toast.open({ message: 'Software update queued.', type: 'is-success' });
  app.proxy.$kya.event('terminals', 'scheduleUpdateSoftware', 'Scheduled software update', { terminalId: terminal.value.id });
  updatingSoftware.value = false;
}
const cancelSoftwareUpdate = async () => {
  if (isUpdatingSoftware.value == true) return; // prevent spam
  isUpdatingSoftware.value = true;
  const result = await store.dispatch('executeTerminalCommand', { id: terminal.value.id, cmd: 'cancelSoftwareUpdate' });
  isUpdatingSoftware.value = false;
  if (!result) { // display message if failed
    app.proxy.$buefy.toast.open({ message: 'Error encountered while attempting to cancel software update.', type: 'is-danger' });
    return;
  }
  app.proxy.$buefy.toast.open({ message: 'Software update cancelled.', type: 'is-success' }); // inform user of success
  app.proxy.$kya.event('terminals', 'cancelSoftwareUpdate', 'Cancelled software update', { terminalId: terminal.value.id });
  updatingSoftware.value = false;
}
// #endregion

const downloadCertificate = () => {
  const data = terminal.value.publicKey;
  const fileName = `${terminal.value.serial}.cer`;
  const mimeType = 'application/x-x509-ca-cert';
  download(data, fileName, mimeType);
  app.proxy.$kya.event('terminals', 'downloadFeatureCertificate', 'Download feature certificate', { terminalId: terminal.value.id });
}
</script>
<style lang="scss">
@import '../../styles/_mixins';

.kpt-terminal-feature-grid {
  @include display-grid;
  grid-template-columns: 200px 1fr;
  margin-bottom: 15px;
}
</style>
